// import { IosSilentNotifications } from '@aparajita/capacitor-ios-silent-notifications'

import { isNative, platform } from '~/plugins/native/capacitor'

// Handle modifications to app icon, be it changing the image or setting unread counts
export default async function ({ store, $log, app, $openWindow, $liveActivity, $analytics, $notify }, inject) {
  const notifications = {}

  if (isNative) {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging')
    const { App } = await import('@capacitor/app')

    notifications.setup = async () => {
      const $app = store.getters['app/getApp']

      if ($app.deviceDetails.isVirtual) {
        $log.debug('Simulator detected, skipping PN registration')

        store.commit('notifications/setPushNotificationPermission', false)
        store.commit('notifications/setPushNotificationDeniedPermission', true)

        return false
      }

      // Always check the actual OS permission status
      const { receive: currentPermission } = await FirebaseMessaging.checkPermissions()

      if (currentPermission === 'granted') {
        // Already have permission, just ensure we have a valid token
        await notifications.getToken()

        return false
      }

      // Request permission — iOS will prompt, Android auto-grants
      store.commit('notifications/setPushNotificationIsLoading', true)

      const { receive } = await FirebaseMessaging.requestPermissions()

      store.commit('notifications/setPushNotificationIsLoading', false)

      store.commit('notifications/setHasAttemptedPushNotificationRegistration', true)

      notifications.onPermissionResult(receive)
    }

    notifications.getToken = async () => {
      const { token } = await FirebaseMessaging.getToken()

      if (token) {
        store.dispatch('notifications/sendPushNotificationToken', token)
        store.commit('notifications/setPushNotificationPermission', true)
        store.commit('notifications/setPushNotificationDeniedPermission', false)

        store.commit('notifications/setPushNotificationRegistration', true)

        $liveActivity.requestTokens()
      }
    }

    notifications.checkPermissions = async () => {
      const { receive } = await FirebaseMessaging.requestPermissions()

      notifications.onPermissionResult(receive)
    }

    notifications.onPermissionResult = async permissionResult => {
      if (permissionResult === 'granted') {
        // $analytics.addEvent('Native Push Notification: Granted')

        // Register with Apple / Google to receive push via APNS/FCM
        await notifications.getToken()

        store.commit('notifications/setPushNotificationPermission', true)
        store.commit('notifications/setPushNotificationDeniedPermission', false)
      } else if (permissionResult === 'denied') {
        $analytics.addEvent('Native Push Notification: Denied')

        store.commit('notifications/setPushNotificationPermission', false)
        store.commit('notifications/setPushNotificationDeniedPermission', true)
      }
    }

    // On success, we should be able to receive notifications
    FirebaseMessaging.addListener('tokenReceived', event => {
      $log.debug('Native PN: Registered', event.token)

      store.dispatch('notifications/sendPushNotificationToken', event.token)

      store.commit('notifications/setPushNotificationRegistration', true)
    })

    // Some issue with our setup and push will not work
    // PushNotifications.addListener('registrationError', error => {
    //   $log.debug('Native PN: Error on registration', error?.error || error)

    //   store.commit('notifications/setPushNotificationPermission', false)
    //   store.commit('notifications/setPushNotificationDeniedPermission', true)
    //   store.commit('notifications/setPushNotificationRegistration', false)

    //   // $notify.error('Error registering Push Notifications, please try again')
    // })

    // Show us the notification payload if the app is open on our device
    FirebaseMessaging.addListener('notificationReceived', async ({ notification }) => {
      $log.debug('Native PN: Received while app open', notification)

      store.commit('debug/addLogEntry', {
        type: 'push-received',
        title: notification?.data?.title || notification?.title || '',
        templateId: notification?.data?.templateId || '',
        data: JSON.parse(JSON.stringify(notification?.data || {}))
      })

      // Handle live activity dismiss silent push
      if (notification?.data?.action === 'dismiss-live-activity' && platform === 'ios') {
        $log.debug('Received dismiss-live-activity push, ending live activity')
        $liveActivity.end(notification.data.bookingId)
        return
      }

      const notificationTitle = notification?.data?.title || notification?.title || ''
      const notificationBody = notification?.data?.body || notification?.body || ''

      // iOS will show a push notification over the app while open.
      // Android will not, so we need to show our own in-app notification
      if (platform === 'android') {
        const appState = await App.getState()

        if (appState?.isActive) {
          $notify.information(notificationTitle, notificationBody)
        }
      }
    })

    // Method called when tapping on a notification
    FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
      $log.debug('Native PN: Opened', JSON.stringify(notification))

      store.commit('debug/addLogEntry', {
        type: 'push-tapped',
        title: notification?.data?.title || notification?.title || '',
        data: JSON.parse(JSON.stringify(notification?.data || {}))
      })

      // Report open event to Courier
      if (notification.data?.trackingUrl) {
        fetch(notification.data.trackingUrl, {
          method: 'POST',
          headers: {
            'X-Courier-Client-Key': process.env.COURIER_CLIENT_KEY
          },
          body: JSON.stringify({
            event: 'OPENED'
          })
        })
      }

      const redirectLink = notification.data?.link ? JSON.parse(notification.data.link) : null

      $analytics.addEvent('Clicked push notification', {
        platform,
        id: notification.data?.pushId || null,
        redirectPath: redirectLink?.path || redirectLink?.href || null
      })

      if (redirectLink) {
        if (redirectLink.path) {
          app.router.push({ path: redirectLink.path })
        } else if (redirectLink.href) {
          $openWindow(redirectLink.href, true)
        }
      }
    })

    // Check permissions if app comes back to the foreground, i.e the user has come back from adjusting settings
    App.addListener('appStateChange', appState => {
      if (
        appState.isActive &&
        store.getters['notifications/hasAttemptedPushNotificationRegistration'] &&
        !store.getters['notifications/hasDeniedPushNotificationPermission']
      ) {
        notifications.checkPermissions()
      }
    })

    // Check the current permission status on app boot so the store reflects
    // whether push notifications are already granted or denied
    const { receive: initialPermission } = await FirebaseMessaging.checkPermissions()

    if (initialPermission === 'granted') {
      await notifications.getToken()
    } else if (initialPermission === 'denied') {
      store.commit('notifications/setPushNotificationPermission', false)
      store.commit('notifications/setPushNotificationDeniedPermission', true)
    }

    // await IosSilentNotifications.addListener('onSilentNotification', notification => {
    //   $log.debug('Silent push notification received:', JSON.stringify(notification))

    //   if (
    //     (notification?.data?.templateId === 'live-activity-end-silent' ||
    //       notification?.data?.templateId === 'live-activity-start-silent') &&
    //     platform === 'ios'
    //   ) {
    //     $liveActivity.start({
    //       bookingId: notification.data.bookingId,
    //       startAt: notification.data.startAt,
    //       endAt: notification.data.endAt,
    //       paddockName: notification.data.paddockName
    //     })
    //   }
    // })
  }

  // Inject to Nuxt context as $pushNotifications
  inject('pushNotifications', notifications)
}
